// client/src/pages/Dashboard.jsx
import { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "../context/ConfirmContext.jsx";
import {
  LayoutDashboard,
  Home,
  Users,
  UserCircle,
  Table,
  Calendar,
  MessageSquare,
  FileText,
  ClipboardList,
  ShoppingBag,
  Boxes,
  Megaphone,
  Globe,
  Menu,
  ChevronLeft,
} from "lucide-react";
import logo from "../assets/logo.png";
import { getImageUrl } from "../utils/imageUrl";
import { getRestaurantes } from "../api/restaurantes.js";

export default function Dashboard() {
  const { usuario, cerrarSesion } = useAuth();
  const { notify } = useConfirm();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [perfilOpen, setPerfilOpen] = useState(false);
  const [restaurante, setRestaurante] = useState(null);
  const perfilRef = useRef(null);

  const handleLogout = async () => {
    localStorage.removeItem("dashboard_modulo_activo");
    await cerrarSesion();
    window.location.href = "/";
  };

  useEffect(() => {
    const fetchRestaurante = async () => {
      try {
        const data = await getRestaurantes();
        const restaurantes = data.restaurantes || [];

        // Si es SuperAdmin, mostrar mensaje genérico
        if (usuario?.isSuperAdmin) {
          setRestaurante({
            nombre_comercial: "Panel del Administrador",
            razon_social: "Sistema Multi-Empresa",
          });
        } else {
          // Si es usuario normal, buscar su restaurante por id_restaurante
          const miRestaurante = restaurantes.find(
            (r) => r.id_restaurante === usuario?.id_restaurante
          );
          if (miRestaurante) {
            setRestaurante(miRestaurante);
          }
        }
      } catch (error) {
        console.error("Error obteniendo restaurante:", error);
      }
    };

    if (usuario) {
      fetchRestaurante();
    }
  }, [usuario]);

  // Efecto para cerrar el menú de perfil al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (perfilRef.current && !perfilRef.current.contains(event.target)) {
        setPerfilOpen(false);
      }
    };

    if (perfilOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [perfilOpen]);

  const modulos = [
    { nombre: "Inicio", ruta: "/dashboard", icono: LayoutDashboard },
    { nombre: "Restaurante", ruta: "/dashboard/restaurante", icono: Home },
    { nombre: "Usuarios", ruta: "/dashboard/usuarios", icono: Users },
    { nombre: "Clientes", ruta: "/dashboard/clientes", icono: UserCircle },
    { nombre: "Productos", ruta: "/dashboard/productos", icono: ShoppingBag },
    { nombre: "Mesas", ruta: "/dashboard/mesas", icono: Table },
    { nombre: "Reservas", ruta: "/dashboard/reservas", icono: Calendar },
    { nombre: "Pedidos", ruta: "/dashboard/pedidos", icono: ClipboardList },
    { nombre: "Ventas", ruta: "/dashboard/ventas", icono: FileText },
    { nombre: "Inventarios", ruta: "/dashboard/inventarios", icono: Boxes },
    { nombre: "Publicaciones", ruta: "/dashboard/publicaciones", icono: Megaphone },
    { nombre: "Reseñas", ruta: "/dashboard/resenias", icono: MessageSquare },
    { nombre: "Redes Sociales", ruta: "/dashboard/redes-sociales", icono: Globe },
  ];

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* SIDEBAR IZQUIERDO - Visible en desktop, oculto en móvil */}
      <aside
        className={`bg-white shadow-md border-r border-gray-200 transition-all duration-300 hidden md:flex flex-col ${
          sidebarCollapsed ? "w-20" : "w-64"
        }`}
      >
        {/* Logo y botón colapsar */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          {!sidebarCollapsed && (
            <Link
              to="/dashboard"
              className="focus:outline-none hover:opacity-80 transition"
              title="Ir al Inicio"
            >
              <img
                src={getImageUrl(restaurante?.logo) || logo}
                alt="Logo Restaurante"
                className="h-16 w-auto object-contain cursor-pointer"
              />
            </Link>
          )}

          {/* Botón de colapsar siempre visible */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-2 rounded hover:bg-gray-100 transition cursor-pointer"
            aria-label={sidebarCollapsed ? "Expandir menú" : "Colapsar menú"}
          >
            {sidebarCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        {/* Menú de módulos en Sidebar (Desktop) */}
        <nav className="flex-1 overflow-auto mt-4 px-3">
          <ul className="flex flex-col gap-1">
            {modulos.map(({ nombre, ruta, icono: Icon }) => (
              <li key={nombre}>
                <NavLink
                  to={ruta}
                  end={ruta === "/dashboard"}
                  className={({ isActive }) =>
                    `flex items-center w-full p-3 rounded-lg transition font-medium text-sm ${
                      isActive
                        ? "bg-amber-100 text-amber-900 font-semibold shadow-xs"
                        : "text-gray-700 hover:bg-gray-100 hover:text-amber-800"
                    }`
                  }
                  title={sidebarCollapsed ? nombre : undefined}
                >
                  <Icon size={19} className="shrink-0" />
                  {!sidebarCollapsed && <span className="ml-3 truncate">{nombre}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* CONTENIDO PRINCIPAL */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Barra superior Header con Menú Horizontal en Móvil */}
        <header className="bg-amber-800 text-white shadow-md sticky top-0 z-40">
          <div className="py-2.5 sm:py-4 px-3 sm:px-6 flex justify-between items-center">
            <div className="flex items-center gap-2.5 min-w-0">
              {/* Mini Logo en Móvil */}
              <Link to="/dashboard" className="md:hidden shrink-0">
                <img
                  src={getImageUrl(restaurante?.logo) || logo}
                  alt="Logo"
                  className="h-8 w-auto object-contain"
                />
              </Link>
              <div className="min-w-0">
                <h1 className="text-sm sm:text-xl font-bold truncate">
                  {restaurante?.nombre_comercial || "Cargando..."}
                </h1>
                {restaurante?.razon_social &&
                  restaurante.razon_social !== restaurante.nombre_comercial && (
                    <p className="text-xs sm:text-sm text-amber-100 truncate hidden xs:block">
                      {restaurante.razon_social}
                    </p>
                  )}
              </div>
            </div>

            {/* Menú usuario */}
            <div className="relative shrink-0" ref={perfilRef}>
              <button
                onClick={() => setPerfilOpen(!perfilOpen)}
                className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-lg hover:bg-amber-700 transition cursor-pointer"
              >
                <UserCircle size={20} className="shrink-0" />
                <span className="text-xs sm:text-sm font-medium max-w-[120px] sm:max-w-none truncate">
                  {usuario?.nombre?.split(' ')[0] || usuario?.correo}
                  {usuario?.rol ? ` (${usuario.rol === 'superAdmin' ? 'Super Admin' : usuario.rol})` : ''}
                </span>
              </button>

              {perfilOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white text-gray-800 shadow-xl rounded-lg border border-gray-100 py-1 z-50 animate-in fade-in zoom-in-95 duration-100">
                  <button
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm font-medium cursor-pointer"
                    onClick={() => {
                      setPerfilOpen(false);
                      notify.info("Mi perfil");
                    }}
                  >
                    Mi perfil
                  </button>
                  <button
                    className="block w-full text-left px-4 py-2 hover:bg-red-50 text-red-600 text-sm font-medium cursor-pointer"
                    onClick={() => {
                      setPerfilOpen(false);
                      handleLogout();
                    }}
                  >
                    Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Barra de Menú de Módulos Horizontal en Header para Móvil */}
          <nav className="md:hidden overflow-x-auto no-scrollbar bg-amber-900/90 border-t border-amber-700/60 px-2 py-1.5 flex items-center gap-1.5 scroll-smooth">
            {modulos.map(({ nombre, ruta, icono: Icon }) => (
              <NavLink
                key={nombre}
                to={ruta}
                end={ruta === "/dashboard"}
                className={({ isActive }) =>
                  `flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold shrink-0 transition-colors ${
                    isActive
                      ? "bg-white text-amber-900 shadow-xs font-bold"
                      : "text-amber-100 hover:bg-amber-800 hover:text-white"
                  }`
                }
              >
                <Icon size={14} className="shrink-0" />
                <span>{nombre}</span>
              </NavLink>
            ))}
          </nav>
        </header>

        {/* Área de contenido renderizada por React Router (Subrutas) */}
        <main className="flex-1 p-3 sm:p-6 overflow-auto bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
